/**
 * Print-Only Tasks Page for PDF Generation
 * Renders tasks in List, Calendar, or Gantt view based on current filters
 *
 * URL: /print/tasks/:businessId
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../../print.css';
import { formatDate, toLocalDateString, getUserTimezone } from '../../utils/dateUtils';

const PrintTasks = () => {
  const { businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [businessName, setBusinessName] = useState('');
  const [error, setError] = useState(null);
  const userTimezone = getUserTimezone();

  // Get URL parameters for filters and view mode
  const urlParams = new URLSearchParams(window.location.search);
  const viewMode = urlParams.get('view_mode') || 'list';
  const calendarPeriod = urlParams.get('calendarPeriod') || 'month';
  const calendarDate = urlParams.get('calendarDate') ? new Date(urlParams.get('calendarDate')) : new Date();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get token from URL parameter (for PDF generation) or localStorage (for direct viewing)
        const urlToken = urlParams.get('token');
        const token = urlToken || localStorage.getItem('token') || sessionStorage.getItem('token');

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Business-Id': businessId
        };


        // Fetch business profile for name
        const profileResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/business/profile`, { headers });
        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          setBusinessName(profileData.data.profile.business_name || 'Business');
        }

        // Build task API URL with SERVER-SIDE filters only (question_id, category, status, priority)
        const taskParams = new URLSearchParams();
        taskParams.append('page', '1');
        taskParams.append('per_page', '500'); // Load all tasks

        // Map frontend filter names to backend parameter names
        const filterCategory = urlParams.get('filterCategory');
        const filterQuestionId = urlParams.get('filterQuestionId');
        const statusFilter = urlParams.get('filter');
        const priorityFilter = urlParams.get('priorityFilter');

        if (filterQuestionId) {
          taskParams.append('question_id', filterQuestionId);
        } else if (filterCategory && filterCategory !== 'all' && filterCategory !== '') {
          taskParams.append('category', filterCategory);
        }
        if (statusFilter && statusFilter !== 'all' && statusFilter !== '') {
          taskParams.append('status', statusFilter);
        }
        if (priorityFilter && priorityFilter !== 'all' && priorityFilter !== '') {
          taskParams.append('priority', priorityFilter);
        }

        const tasksUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/tasks?${taskParams.toString()}`;

        const tasksResponse = await fetch(tasksUrl, { headers });

        if (!tasksResponse.ok) {
          throw new Error('Failed to fetch tasks');
        }

        const tasksData = await tasksResponse.json();

        // Apply CLIENT-SIDE filters
        let filteredTasks = tasksData.tasks || [];

        // Activity type filter
        const activityTypeFilter = urlParams.get('activityTypeFilter');
        if (activityTypeFilter && activityTypeFilter !== 'all') {
          filteredTasks = filteredTasks.filter(task =>
            task.question?.activity_type === activityTypeFilter ||
            task.activity_type === activityTypeFilter
          );
        }

        // Intangible capital filter
        const intangibleCapitalFilter = urlParams.get('intangibleCapitalFilter');
        if (intangibleCapitalFilter && intangibleCapitalFilter !== 'all') {
          filteredTasks = filteredTasks.filter(task =>
            task.question?.intangible_asset_type === intangibleCapitalFilter ||
            task.intangible_capital_type === intangibleCapitalFilter
          );
        }

        // Due date filter
        const dueDateFilter = urlParams.get('dueDateFilter');
        if (dueDateFilter && dueDateFilter !== 'all') {
          const now = new Date();
          filteredTasks = filteredTasks.filter(task => {
            if (!task.due_date) return dueDateFilter === 'no_due_date';

            // Parse date-only string properly without UTC conversion
            const [year, month, day] = task.due_date.split('-').map(Number);
            const dueDate = new Date(year, month - 1, day);
            const daysUntilDue = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));

            switch (dueDateFilter) {
              case 'overdue':
                return daysUntilDue < 0;
              case 'today':
                return daysUntilDue === 0;
              case 'this_week':
                return daysUntilDue >= 0 && daysUntilDue <= 7;
              case 'this_month':
                return daysUntilDue >= 0 && daysUntilDue <= 30;
              case 'no_due_date':
                return false;
              default:
                return true;
            }
          });
        }

        // Gap score filter
        const gapScoreFilter = urlParams.get('gapScoreFilter');
        if (gapScoreFilter && gapScoreFilter !== 'all') {
          filteredTasks = filteredTasks.filter(task => {
            const score = task.question?.gap_score;
            if (score === undefined || score === null) return false;

            switch (gapScoreFilter) {
              case 'high':
                return score >= 3;
              case 'medium':
                return score === 2;
              case 'low':
                return score <= 1;
              default:
                return true;
            }
          });
        }

        // Assessment type filter
        const assessmentTypeFilter = urlParams.get('assessmentTypeFilter');
        if (assessmentTypeFilter && assessmentTypeFilter !== 'all') {
          filteredTasks = filteredTasks.filter(task => {
            const taskAssessmentType = task.question?.assessment_type || task.assessment_type;
            if (!taskAssessmentType) return false;

            // Handle "business" and "personal" filter selections
            // "business" filter should match both "business" and "family enterprise"
            // "personal" filter should match "personal financial", "personal relational", "personal spiritual"
            if (assessmentTypeFilter === 'business') {
              return taskAssessmentType === 'business' || taskAssessmentType === 'family enterprise';
            } else if (assessmentTypeFilter === 'personal') {
              return taskAssessmentType.startsWith('personal');
            }

            // For exact matches (shouldn't happen from the filter dropdown, but support it anyway)
            return taskAssessmentType === assessmentTypeFilter;
          });
        }


        // Apply sorting
        const sortBy = urlParams.get('sortBy') || 'created_at';
        const sortOrder = urlParams.get('sortOrder') || 'desc';

        filteredTasks.sort((a, b) => {
          let aValue, bValue;

          switch (sortBy) {
            case 'title':
              aValue = a.title.toLowerCase();
              bValue = b.title.toLowerCase();
              break;
            case 'priority':
              const priorityOrder = { high: 3, medium: 2, low: 1 };
              aValue = priorityOrder[a.priority] || 0;
              bValue = priorityOrder[b.priority] || 0;
              break;
            case 'status':
              const statusOrder = { not_started: 1, in_progress: 2, under_review: 3, completed: 4, not_relevant: 5 };
              aValue = statusOrder[a.status] || 0;
              bValue = statusOrder[b.status] || 0;
              break;
            case 'due_date':
              aValue = a.due_date ? new Date(a.due_date).getTime() : 0;
              bValue = b.due_date ? new Date(b.due_date).getTime() : 0;
              break;
            case 'start_date':
              aValue = a.start_date ? new Date(a.start_date).getTime() : 0;
              bValue = b.start_date ? new Date(b.start_date).getTime() : 0;
              break;
            case 'gap_score':
              aValue = a.question?.gap_score || 0;
              bValue = b.question?.gap_score || 0;
              break;
            case 'category':
              aValue = a.question?.category_display || '';
              bValue = b.question?.category_display || '';
              break;
            case 'activity_type':
              aValue = a.question?.activity_type || '';
              bValue = b.question?.activity_type || '';
              break;
            case 'intangible_asset_type':
              aValue = a.question?.intangible_asset_type || '';
              bValue = b.question?.intangible_asset_type || '';
              break;
            case 'created_at':
            default:
              aValue = new Date(a.created_at).getTime();
              bValue = new Date(b.created_at).getTime();
              break;
          }

          if (sortOrder === 'asc') {
            return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
          } else {
            return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
          }
        });

        setTasks(filteredTasks);
        setLoading(false);
      } catch (err) {
        console.error('[PrintTasks] Error loading tasks:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId]);

  useEffect(() => {
    if (!loading) {
      document.body.setAttribute('data-pdf-ready', 'true');
      document.title = `${businessName} - Task Manager ${viewMode} View`;
    }
  }, [loading, businessName, viewMode]);

  if (loading) {
    return (
      <div className="print-container" data-pdf-loading="true">
        <div className="loading">Loading tasks...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="print-container" data-pdf-error="true">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const viewModeTitle = {
    'list': 'List View',
    'calendar': 'Calendar View',
    'gantt': 'Gantt View'
  }[viewMode] || 'List View';

  // Helper function to get status badge style
  const getStatusStyle = (status) => {
    const styles = {
      'not_started': { bg: '#fee2e2', text: '#991b1b', label: 'Not Started' },
      'in_progress': { bg: '#dbeafe', text: '#1e3a8a', label: 'In Progress' },
      'under_review': { bg: '#e9d5ff', text: '#6b21a8', label: 'Under Review' },
      'completed': { bg: '#dcfce7', text: '#14532d', label: 'Completed' },
      'not_relevant': { bg: '#f3f4f6', text: '#4b5563', label: 'Not Relevant' }
    };
    return styles[status] || styles['not_started'];
  };

  // Helper function to get priority badge style
  const getPriorityStyle = (priority) => {
    const styles = {
      'high': { bg: '#fee2e2', text: '#991b1b', label: 'High' },
      'medium': { bg: '#fed7aa', text: '#9a3412', label: 'Medium' },
      'low': { bg: '#d1fae5', text: '#065f46', label: 'Low' }
    };
    return styles[priority] || styles['low'];
  };

  // Helper function to get readable assessment type label
  // Maps the 5 distinct assessment types from the database
  const getAssessmentTypeLabel = (assessmentType) => {
    const labels = {
      // Business Gap Assessment types (from assessment_questions table)
      'business': 'Business',
      'family enterprise': 'Family Enterprise',
      // Personal Gap Assessment types (from personal_assessment_questions table)
      'personal financial': 'Personal Financial',
      'personal relational': 'Personal Wellbeing',
      'personal spiritual': 'Personal Spiritual'
    };
    return labels[assessmentType] || assessmentType;
  };

  // Helper function to parse and format notes
  const parseNotes = (notes) => {
    if (!notes) return null;

    try {
      // Try to parse as JSON array
      const parsed = JSON.parse(notes);
      if (Array.isArray(parsed)) {
        // Extract text from each note object and join with line breaks
        return parsed
          .map(note => note.text || '')
          .filter(text => text.trim())
          .join('\n\n');
      }
      // If it's not an array, return as is
      return notes;
    } catch (e) {
      // If JSON parsing fails, assume it's plain text
      return notes;
    }
  };

  // Render Cover Page
  const renderCoverPage = (taskCount) => (
    <div className="print-page cover-page">
      <div className="pdf-header">
        <img src="/dominion-header.png" alt="Dominion Business Advisors" />
      </div>
      <div className="cover-header">
        <h1>Task Manager Report</h1>
        <div className="report-type">{viewModeTitle}</div>
      </div>

      <div className="cover-business-info">
        <h2>{businessName}</h2>
        <p className="task-count" style={{ color: '#f1e9dd' }}>{taskCount} Task{taskCount !== 1 ? 's' : ''}</p>
      </div>

      <div className="cover-footer">
        <p>Generated: {today}</p>
        <p className="confidential">Confidential Task Management Report</p>
      </div>
    </div>
  );

  // Render List View
  const renderListView = () => {
    if (tasks.length === 0) {
      return (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p style={{ fontSize: '1.125rem', color: '#6b7280' }}>No tasks found matching the current filters.</p>
          </div>
        </div>
      );
    }

    // Group tasks into pages (2 tasks per page to prevent bleeding)
    const tasksPerPage = 2;
    const pages = [];
    for (let i = 0; i < tasks.length; i += tasksPerPage) {
      pages.push(tasks.slice(i, i + tasksPerPage));
    }

    return pages.map((pageTasks, pageIndex) => (
      <div key={pageIndex} className="print-page">
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <div style={{ padding: '1.5rem' }}>
          {pageIndex === 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
                Task Manager - List View
              </h1>
              <p style={{ color: '#4b5563', fontSize: '0.75rem', lineHeight: '1.5', display: 'flex', gap: '0.75rem', flexWrap: 'nowrap', alignItems: 'center' }}>
                <span style={{ fontWeight: '600', color: '#111827' }}>{businessName}</span>
                <span style={{ color: '#d1d5db' }}>|</span>
                <span><strong>Today:</strong> {today}</span>
                <span style={{ color: '#d1d5db' }}>|</span>
                <span><strong>{tasks.length}</strong> Tasks</span>
              </p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {pageTasks.map((task) => {
              const statusStyle = getStatusStyle(task.status);
              const priorityStyle = getPriorityStyle(task.priority);

              return (
                <div
                  key={task.id}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderLeft: `4px solid ${priorityStyle.bg}`,
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    backgroundColor: '#ffffff',
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', flex: 1 }}>
                      {task.title}
                    </h3>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <span
                        style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          backgroundColor: statusStyle.bg,
                          color: statusStyle.text
                        }}
                      >
                        {statusStyle.label}
                      </span>
                      <span
                        style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          backgroundColor: priorityStyle.bg,
                          color: priorityStyle.text
                        }}
                      >
                        {priorityStyle.label} Priority
                      </span>
                    </div>
                  </div>

                  {task.description && (
                    <p style={{ fontSize: '0.875rem', color: '#4b5563', marginBottom: '0.75rem', lineHeight: '1.5' }}>
                      {task.description}
                    </p>
                  )}

                  {task.notes && parseNotes(task.notes) && (
                    <div style={{
                      marginTop: '0.75rem',
                      marginBottom: '0.75rem',
                      padding: '0.75rem',
                      backgroundColor: '#f9fafb',
                      borderRadius: '0.375rem',
                      borderLeft: '3px solid #3b82f6'
                    }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>Notes:</div>
                      <p style={{ fontSize: '0.875rem', color: '#4b5563', lineHeight: '1.5', margin: 0, whiteSpace: 'pre-line' }}>
                        {parseNotes(task.notes)}
                      </p>
                    </div>
                  )}

                  {(task.question?.question_text || task.question_text) && (
                    <div style={{
                      marginTop: '0.75rem',
                      marginBottom: '0.75rem',
                      padding: '0.5rem',
                      backgroundColor: '#f0f9ff',
                      borderRadius: '0.375rem',
                      borderLeft: '3px solid #0ea5e9'
                    }}>
                      <div style={{ fontSize: '0.55rem', fontWeight: '600', color: '#075985', marginBottom: '0.25rem' }}>Assessment Question: 
                        <span style={{ fontSize: '0.55rem', color: '#0c4a6e', lineHeight: '1.4', marginLeft: '0.5rem' }}>{task.question?.question_text || task.question_text}</span> 
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', fontSize: '0.75rem', marginTop: '0.75rem' }}>
                    {task.start_date && (
                      <div style={{ color: '#6b7280' }}>
                        <strong>Start:</strong> {formatDate(task.start_date, userTimezone)}
                      </div>
                    )}
                    {task.due_date && (
                      <div style={{ color: '#6b7280' }}>
                        <strong>Due:</strong> {formatDate(task.due_date, userTimezone)}
                      </div>
                    )}
                    {(task.question?.assessment_type || task.assessment_type) && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.25rem 0.625rem',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem'
                      }}>
                        <div style={{ width: '0.5rem', height: '0.5rem', borderRadius: '9999px', backgroundColor: '#6b7280' }}></div>
                        <span style={{ fontSize: '0.55rem', fontWeight: '500', color: '#374151' }}>Assessment Type: {getAssessmentTypeLabel(task.question?.assessment_type || task.assessment_type)}</span>
                      </div>
                    )}
                    {task.question && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.25rem 0.625rem',
                        backgroundColor: '#fdf4ff',
                        border: '1px solid #f5d0fe',
                        borderRadius: '0.375rem'
                      }}>
                        <div style={{ width: '0.5rem', height: '0.5rem', borderRadius: '9999px', backgroundColor: '#d946ef' }}></div>
                        <span style={{ fontSize: '0.55rem', fontWeight: '500', color: '#a21caf' }}>Category: {task.question.category_display}</span>
                      </div>
                    )}
                    {(task.question?.subject) && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.25rem 0.625rem',
                        backgroundColor: '#ecfeff',
                        border: '1px solid #a5f3fc',
                        borderRadius: '0.375rem'
                      }}>
                        <div style={{ width: '0.5rem', height: '0.5rem', borderRadius: '9999px', backgroundColor: '#06b6d4' }}></div>
                        <span style={{ fontSize: '0.55rem', fontWeight: '500', color: '#0e7490' }}>Theme: {task.question.subject}</span>
                      </div>
                    )}
                    {(task.question?.activity_type || task.activity_type) && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.25rem 0.625rem',
                        backgroundColor: '#ecfdf5',
                        border: '1px solid #a7f3d0',
                        borderRadius: '0.375rem'
                      }}>
                        <div style={{ width: '0.5rem', height: '0.5rem', borderRadius: '9999px', backgroundColor: '#10b981' }}></div>
                        <span style={{ fontSize: '0.55rem', fontWeight: '500', color: '#047857' }}>Activity: {task.question?.activity_type || task.activity_type}</span>
                      </div>
                    )}
                    {(task.question?.intangible_asset_type || task.intangible_capital_type) && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        padding: '0.25rem 0.625rem',
                        backgroundColor: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderRadius: '0.375rem'
                      }}>
                        <div style={{ width: '0.5rem', height: '0.5rem', borderRadius: '9999px', backgroundColor: '#f59e0b' }}></div>
                        <span style={{ fontSize: '0.55rem', fontWeight: '500', color: '#b45309' }}>Intangibles: {task.question?.intangible_asset_type || task.intangible_capital_type}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    ));
  };

  // Render Calendar View
  const renderCalendarView = () => {
    // Group tasks by due date
    const tasksByDate = {};
    tasks.forEach(task => {
      if (task.due_date) {
        const dateKey = task.due_date.split('T')[0];
        if (!tasksByDate[dateKey]) {
          tasksByDate[dateKey] = [];
        }
        tasksByDate[dateKey].push(task);
      }
    });

    // Get calendar dates based on period - returns array of months for multi-month views
    const getCalendarMonths = () => {
      const startDate = new Date(calendarDate);
      const months = [];

      if (calendarPeriod === '3month') {
        // Show 3 months
        for (let i = 0; i < 3; i++) {
          const monthDate = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
          months.push(monthDate);
        }
      } else if (calendarPeriod === '6month') {
        // Show 6 months
        for (let i = 0; i < 6; i++) {
          const monthDate = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
          months.push(monthDate);
        }
      } else {
        // Single period (5day, 7day, month)
        months.push(startDate);
      }

      return months;
    };

    // Get calendar dates for a single month
    const getMonthDates = (monthDate) => {
      const dates = [];
      const startDate = new Date(monthDate);

      if (calendarPeriod === '5day') {
        // Get Monday of current week
        const day = startDate.getDay();
        const diff = startDate.getDate() - day + (day === 0 ? -6 : 1);
        startDate.setDate(diff);

        for (let i = 0; i < 5; i++) {
          const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
          dates.push(date);
        }
      } else if (calendarPeriod === '7day') {
        // Get Sunday of current week
        const day = startDate.getDay();
        startDate.setDate(startDate.getDate() - day);

        for (let i = 0; i < 7; i++) {
          const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
          dates.push(date);
        }
      } else {
        // Month view - get all days in month
        const year = startDate.getFullYear();
        const month = startDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        // Start from Sunday before first day of month
        const startDay = new Date(firstDay);
        startDay.setDate(startDay.getDate() - firstDay.getDay());

        let currentDate = new Date(startDay);
        while (currentDate <= lastDay || currentDate.getDay() !== 0) {
          dates.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
          if (dates.length > 42) break; // Max 6 weeks
        }
      }

      return dates;
    };

    const calendarMonths = getCalendarMonths();
    const isMultiMonth = calendarPeriod === '3month' || calendarPeriod === '6month';

    // Get date range for filtering tasks - use actual month boundaries, not calendar padding
    let calendarStartDate, calendarEndDate;

    if (calendarPeriod === '5day' || calendarPeriod === '7day') {
      // For week views, use the actual week dates
      const allCalendarDates = calendarMonths.flatMap(month => getMonthDates(month));
      calendarStartDate = new Date(allCalendarDates[0]);
      calendarStartDate.setHours(0, 0, 0, 0); // Normalize to start of day
      calendarEndDate = new Date(allCalendarDates[allCalendarDates.length - 1]);
      calendarEndDate.setHours(23, 59, 59, 999); // Normalize to end of day
    } else {
      // For month views, use actual first and last day of selected months only
      const firstMonth = calendarMonths[0];
      const lastMonth = calendarMonths[calendarMonths.length - 1];

      calendarStartDate = new Date(firstMonth.getFullYear(), firstMonth.getMonth(), 1);
      calendarStartDate.setHours(0, 0, 0, 0); // Normalize to start of day
      calendarEndDate = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0); // Last day of month
      calendarEndDate.setHours(23, 59, 59, 999); // Normalize to end of day
    }

    // Format date range string based on view type
    const getDateRangeText = () => {
      if (calendarPeriod === '5day' || calendarPeriod === '7day') {
        // Week views: "Dec 16 - Dec 20, 2024" or "Dec 29, 2024 - Jan 4, 2025" (for cross-year)
        const startMonth = calendarStartDate.toLocaleDateString('default', { month: 'short' });
        const endMonth = calendarEndDate.toLocaleDateString('default', { month: 'short' });
        const startDay = calendarStartDate.getDate();
        const endDay = calendarEndDate.getDate();
        const startYear = calendarStartDate.getFullYear();
        const endYear = calendarEndDate.getFullYear();

        if (startYear === endYear) {
          if (startMonth === endMonth) {
            return `${startMonth} ${startDay} - ${endDay}, ${startYear}`;
          } else {
            return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startYear}`;
          }
        } else {
          return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
        }
      } else if (calendarPeriod === 'month') {
        // Single month: "December 2024"
        return calendarStartDate.toLocaleDateString('default', { month: 'long', year: 'numeric' });
      } else if (calendarPeriod === '3month' || calendarPeriod === '6month') {
        // Multi-month: "December 2024 - February 2025" or "December 2024 - May 2025"
        const startMonth = calendarStartDate.toLocaleDateString('default', { month: 'long', year: 'numeric' });
        const endMonth = calendarEndDate.toLocaleDateString('default', { month: 'long', year: 'numeric' });
        return `${startMonth} - ${endMonth}`;
      }
      return '';
    };

    // Filter tasks to only include those visible in the selected period
    const visibleTasks = tasks.filter(task => {
      if (!task.due_date) return false;
      // Parse date-only string properly without UTC conversion
      const [year, month, day] = task.due_date.split('-').map(Number);
      const taskDate = new Date(year, month - 1, day);
      taskDate.setHours(0, 0, 0, 0);
      return taskDate >= calendarStartDate && taskDate <= calendarEndDate;
    });

    // Render a single month calendar
    const renderMonth = (monthDate) => {
      const monthDates = getMonthDates(monthDate);
      const isWeekView = calendarPeriod === '5day' || calendarPeriod === '7day';
      const columnsCount = isWeekView ? monthDates.length : 7;
      const weeksCount = isWeekView ? 1 : Math.ceil(monthDates.length / 7);
      const cellHeight = isMultiMonth ? '40px' : (weeksCount <= 5 ? '80px' : '65px');
      const fontSize = isMultiMonth ? '0.6rem' : '0.7rem';

      // Day headers based on view type
      const getDayHeaders = () => {
        if (calendarPeriod === '5day') {
          return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
        } else if (calendarPeriod === '7day') {
          return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        } else {
          return isMultiMonth ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        }
      };

      return (
        <div key={monthDate.toISOString()} style={{ marginBottom: isMultiMonth ? '1rem' : '0' }}>
          {isMultiMonth && (
            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem', textAlign: 'center' }}>
              {monthDate.toLocaleDateString('default', { month: 'long', year: 'numeric' })}
            </h3>
          )}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.375rem', overflow: 'hidden' }}>
            {/* Day headers */}
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columnsCount}, 1fr)`, backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              {getDayHeaders().map((day, i) => (
                <div key={i} style={{ padding: '0.25rem', textAlign: 'center', fontWeight: '600', fontSize: isMultiMonth ? '0.6rem' : '0.75rem', color: '#4b5563' }}>
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            {Array.from({ length: weeksCount }).map((_, weekIndex) => (
              <div key={weekIndex} style={{ display: 'grid', gridTemplateColumns: `repeat(${columnsCount}, 1fr)`, borderBottom: weekIndex < weeksCount - 1 ? '1px solid #e5e7eb' : 'none' }}>
                {monthDates.slice(weekIndex * columnsCount, (weekIndex + 1) * columnsCount).map((date, dayIndex) => {
                  const dateStr = toLocalDateString(date, userTimezone);
                  const dayTasks = tasksByDate[dateStr] || [];
                  const isToday = date.toDateString() === new Date().toDateString();
                  const isCurrentMonth = isWeekView ? true : date.getMonth() === monthDate.getMonth();

                  return (
                    <div
                      key={dayIndex}
                      style={{
                        minHeight: cellHeight,
                        padding: '0.25rem',
                        borderRight: dayIndex < (columnsCount - 1) ? '1px solid #e5e7eb' : 'none',
                        backgroundColor: isToday ? '#eff6ff' : isCurrentMonth ? '#ffffff' : '#f9fafb',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ fontSize, fontWeight: isToday ? '700' : '500', color: isCurrentMonth ? '#111827' : '#9ca3af', marginBottom: '0.125rem' }}>
                        {date.getDate()}
                      </div>
                      {/* Show colored dots for tasks - only for dates in current month */}
                      {dayTasks.length > 0 && isCurrentMonth && (
                        <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap', justifyContent: 'center' }}>
                          {dayTasks.slice(0, 3).map(task => {
                            const priorityStyle = getPriorityStyle(task.priority);
                            return (
                              <div
                                key={task.id}
                                style={{
                                  width: isMultiMonth ? '6px' : '8px',
                                  height: isMultiMonth ? '6px' : '8px',
                                  borderRadius: '50%',
                                  backgroundColor: priorityStyle.bg,
                                  border: `2px solid ${priorityStyle.text}`
                                }}
                                title={task.title}
                              />
                            );
                          })}
                          {dayTasks.length > 3 && (
                            <div style={{ fontSize: isMultiMonth ? '0.5rem' : '0.6rem', color: '#6b7280', fontWeight: '500' }}>
                              +{dayTasks.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      );
    };

    return (
      <>
        {/* Page 2: Calendar Grid */}
        <div className="print-page" style={{ pageBreakAfter: 'always' }}>
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>

          <div style={{ padding: isMultiMonth ? '1rem' : '1.5rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <h1 style={{ fontSize: isMultiMonth ? '1.5rem' : '1.875rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
                Task Manager - Calendar View
              </h1>
              <p style={{ color: '#4b5563', fontSize: '0.75rem', lineHeight: '1.5', display: 'flex', gap: '0.75rem', flexWrap: 'nowrap', alignItems: 'center' }}>
                <span style={{ fontWeight: '600', color: '#111827' }}>{businessName}</span>
                <span style={{ color: '#d1d5db' }}>|</span>
                <span><strong>Date Range:</strong> {getDateRangeText()}</span>
                <span style={{ color: '#d1d5db' }}>|</span>
                <span><strong>Today:</strong> {today}</span>
                <span style={{ color: '#d1d5db' }}>|</span>
                <span><strong>{visibleTasks.length}</strong> Tasks</span>
              </p>
            </div>

            {/* Calendar Grid(s) */}
            {isMultiMonth ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: calendarPeriod === '6month' ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
                gap: '1rem'
              }}>
                {calendarMonths.map(month => renderMonth(month))}
              </div>
            ) : (
              renderMonth(calendarMonths[0])
            )}
          </div>
        </div>

        {/* Page 3+: Task Details List (only visible tasks) - Paginated */}
        {visibleTasks.length > 0 && (() => {
          // Split tasks into pages (6 tasks per page to accommodate descriptions and prevent overflow)
          const tasksPerPage = 6;
          const taskPages = [];
          for (let i = 0; i < visibleTasks.length; i += tasksPerPage) {
            taskPages.push(visibleTasks.slice(i, i + tasksPerPage));
          }

          return taskPages.map((pageTasks, pageIndex) => (
            <div key={`task-page-${pageIndex}`} className="print-page">
              <div className="pdf-header">
                <img src="/dominion-header.png" alt="Dominion Business Advisors" />
              </div>

              <div style={{ padding: '1.5rem' }}>
                {pageIndex === 0 && (
                  <>
                    <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
                      Task Details
                    </h1>
                    <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                      Tasks visible in calendar view: {visibleTasks.length}
                    </p>
                  </>
                )}

                {pageIndex > 0 && (
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginBottom: '1.5rem' }}>
                    Task Details (continued)
                  </h2>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {pageTasks.map((task) => {
                    const statusStyle = getStatusStyle(task.status);
                    const priorityStyle = getPriorityStyle(task.priority);

                    return (
                      <div
                        key={task.id}
                        style={{
                          padding: '0.75rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          pageBreakInside: 'avoid',
                          breakInside: 'avoid'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <strong>{task.title}</strong>
                            {task.due_date && (
                              <span style={{ marginLeft: '0.5rem', color: '#6b7280' }}>
                                • Due: {formatDate(task.due_date, userTimezone)}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <span
                              style={{
                                padding: '0.125rem 0.5rem',
                                borderRadius: '9999px',
                                fontSize: '0.625rem',
                                backgroundColor: statusStyle.bg,
                                color: statusStyle.text
                              }}
                            >
                              {statusStyle.label}
                            </span>
                            <span
                              style={{
                                padding: '0.125rem 0.5rem',
                                borderRadius: '9999px',
                                fontSize: '0.625rem',
                                backgroundColor: priorityStyle.bg,
                                color: priorityStyle.text
                              }}
                            >
                              {priorityStyle.label}
                            </span>
                          </div>
                        </div>
                        {/* Task Description */}
                        {task.description && (
                          <div style={{
                            marginTop: '0.5rem',
                            color: '#4b5563',
                            fontSize: '0.75rem',
                            lineHeight: '1.4',
                            whiteSpace: 'pre-wrap'
                          }}>
                            {task.description}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ));
        })()}
      </>
    );
  };

  // Render Gantt View
  const renderGanttView = () => {
    const tasksWithDates = tasks.filter(task => task.due_date || task.created_at);

    if (tasksWithDates.length === 0) {
      return (
        <div className="print-page">
          <div className="pdf-header">
            <img src="/dominion-header.png" alt="Dominion Business Advisors" />
          </div>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p style={{ fontSize: '1.125rem', color: '#6b7280' }}>No tasks with dates found for Gantt view.</p>
          </div>
        </div>
      );
    }

    // Calculate 6-month timeline: 1 past, current, 4 future
    const now = new Date();
    const ganttStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    ganttStartDate.setHours(0, 0, 0, 0);
    const ganttEndDate = new Date(now.getFullYear(), now.getMonth() + 5, 0); // Last day of 4 months ahead
    ganttEndDate.setHours(23, 59, 59, 999);

    // Generate month headers for the 6-month period
    const months = [];
    for (let i = 0; i < 6; i++) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - 1 + i, 1);
      months.push({
        date: monthDate,
        label: monthDate.toLocaleDateString('default', { month: 'short', year: '2-digit' })
      });
    }

    const totalDays = Math.ceil((ganttEndDate - ganttStartDate) / (1000 * 60 * 60 * 24));

    // Process tasks for gantt display
    const ganttTasks = tasksWithDates.map(task => {
      let startDate = task.start_date ? (() => {
        const [year, month, day] = task.start_date.split('-').map(Number);
        return new Date(year, month - 1, day);
      })() : new Date(task.created_at);

      let dueDate;
      if (task.due_date) {
        const [year, month, day] = task.due_date.split('-').map(Number);
        dueDate = new Date(year, month - 1, day);
      } else {
        dueDate = new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000); // Default 7 days
      }

      startDate.setHours(0, 0, 0, 0);
      dueDate.setHours(23, 59, 59, 999);

      // Calculate position and width as percentages
      const taskStart = Math.max(startDate, ganttStartDate);
      const taskEnd = Math.min(dueDate, ganttEndDate);

      const startOffset = Math.max(0, (taskStart - ganttStartDate) / (1000 * 60 * 60 * 24));
      const duration = Math.max(1, (taskEnd - taskStart) / (1000 * 60 * 60 * 24));

      const leftPercent = (startOffset / totalDays) * 100;
      const widthPercent = (duration / totalDays) * 100;

      return {
        ...task,
        startDate,
        dueDate,
        leftPercent,
        widthPercent,
        isVisible: dueDate >= ganttStartDate && startDate <= ganttEndDate
      };
    }).filter(task => task.isVisible);

    // Render a single Gantt chart page
    const renderGanttPage = (pageTasks, pageIndex, totalPages) => (
      <div key={`gantt-page-${pageIndex}`} className="print-page" style={{ pageBreakAfter: 'always' }}>
        <div className="pdf-header">
          <img src="/dominion-header.png" alt="Dominion Business Advisors" />
        </div>

        <div style={{ padding: '0.5rem' }}>
          <div style={{ marginBottom: '0.5rem' }}>
            {pageIndex === 0 && (
              <h1 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.25rem' }}>
                Task Manager - Gantt View
              </h1>
            )}
            {pageIndex > 0 && (
              <h2 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.25rem' }}>
                Gantt View (continued)
              </h2>
            )}
            <p style={{ color: '#4b5563', fontSize: '0.65rem', lineHeight: '1.3', display: 'flex', gap: '0.5rem', flexWrap: 'nowrap', alignItems: 'center' }}>
              <span style={{ fontWeight: '600', color: '#111827' }}>{businessName}</span>
              <span style={{ color: '#d1d5db' }}>|</span>
              <span><strong>Date Range:</strong> {ganttStartDate.toLocaleDateString('default', { month: 'short', year: 'numeric' })} - {ganttEndDate.toLocaleDateString('default', { month: 'short', year: 'numeric' })}</span>
              <span style={{ color: '#d1d5db' }}>|</span>
              <span><strong>Today:</strong> {today}</span>
              <span style={{ color: '#d1d5db' }}>|</span>
              <span><strong>{ganttTasks.length}</strong> Tasks</span>
            </p>
          </div>

          {/* Gantt Chart Container */}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.25rem', overflow: 'hidden' }}>
            {/* Month Headers */}
            <div style={{ display: 'flex', backgroundColor: '#f9fafb', borderBottom: '2px solid #d1d5db' }}>
              <div style={{ width: '180px', padding: '0.3rem', fontWeight: '600', fontSize: '0.65rem', borderRight: '2px solid #d1d5db' }}>
                Task
              </div>
              <div style={{ width: '60px', padding: '0.3rem', textAlign: 'center', fontWeight: '600', fontSize: '0.6rem', borderRight: '2px solid #d1d5db' }}>
                Due Date
              </div>
              <div style={{ flex: 1, display: 'flex' }}>
                {months.map((month, idx) => (
                  <div key={idx} style={{
                    flex: 1,
                    padding: '0.3rem',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '0.6rem',
                    borderRight: idx < months.length - 1 ? '1px solid #e5e7eb' : 'none'
                  }}>
                    {month.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Today indicator line */}
            <div style={{ position: 'relative', height: '0' }}>
              {(() => {
                const todayOffset = (now - ganttStartDate) / (1000 * 60 * 60 * 24);
                const todayPercent = (todayOffset / totalDays) * 100;
                if (todayPercent >= 0 && todayPercent <= 100) {
                  return (
                    <div style={{
                      position: 'absolute',
                      left: `calc(240px + ${todayPercent}% * (100% - 240px) / 100)`,
                      width: '2px',
                      height: '100vh',
                      backgroundColor: '#3b82f6',
                      zIndex: 1,
                      opacity: 0.5
                    }} />
                  );
                }
                return null;
              })()}
            </div>

            {/* Task Rows */}
            {pageTasks.map((task, idx) => {
              const priorityStyle = getPriorityStyle(task.priority);
              const statusStyle = getStatusStyle(task.status);

              return (
                <div key={task.id} style={{
                  display: 'flex',
                  borderBottom: idx < pageTasks.length - 1 ? '1px solid #e5e7eb' : 'none',
                  backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                  minHeight: '2.2rem',
                  alignItems: 'center'
                }}>
                  {/* Task Name */}
                  <div style={{
                    width: '180px',
                    padding: '0.3rem',
                    fontSize: '0.6rem',
                    fontWeight: '500',
                    borderRight: '2px solid #d1d5db',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {task.title}
                  </div>

                  {/* Due Date */}
                  <div style={{
                    width: '60px',
                    padding: '0.3rem',
                    textAlign: 'center',
                    borderRight: '2px solid #d1d5db',
                    fontSize: '0.55rem',
                    color: '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {task.dueDate.toLocaleDateString('default', { month: 'short', day: 'numeric' })}
                  </div>

                  {/* Timeline Bar */}
                  <div style={{ flex: 1, position: 'relative', height: '100%', display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      position: 'absolute',
                      left: `${task.leftPercent}%`,
                      width: `${task.widthPercent}%`,
                      height: '1.4rem',
                      backgroundColor: priorityStyle.bg,
                      border: `2px solid ${priorityStyle.text}`,
                      borderRadius: '0.2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.55rem',
                      fontWeight: '600',
                      color: priorityStyle.text,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap'
                    }}>
                      <span style={{ padding: '0 0.2rem' }}>
                        {Math.ceil((task.dueDate - task.startDate) / (1000 * 60 * 60 * 24))}d
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );

    // Paginate Gantt tasks (20 per page)
    const ganttTasksPerPage = 20;
    const ganttPages = [];
    for (let i = 0; i < ganttTasks.length; i += ganttTasksPerPage) {
      ganttPages.push(ganttTasks.slice(i, i + ganttTasksPerPage));
    }

    return (
      <>
        {/* Gantt Chart Pages */}
        {ganttPages.map((pageTasks, pageIndex) =>
          renderGanttPage(pageTasks, pageIndex, ganttPages.length)
        )}

        {/* Page 3+: Task Details (Paginated) */}
        {ganttTasks.length > 0 && (() => {
          const tasksPerPage = 6;
          const taskPages = [];
          for (let i = 0; i < ganttTasks.length; i += tasksPerPage) {
            taskPages.push(ganttTasks.slice(i, i + tasksPerPage));
          }

          return taskPages.map((pageTasks, pageIndex) => (
            <div key={`task-page-${pageIndex}`} className="print-page">
              <div className="pdf-header">
                <img src="/dominion-header.png" alt="Dominion Business Advisors" />
              </div>

              <div style={{ padding: '1.5rem' }}>
                {pageIndex === 0 && (
                  <>
                    <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
                      Task Details
                    </h1>
                    <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
                      Tasks in Gantt view: {ganttTasks.length}
                    </p>
                  </>
                )}

                {pageIndex > 0 && (
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#111827', marginBottom: '1.5rem' }}>
                    Task Details (continued)
                  </h2>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {pageTasks.map((task) => {
                    const statusStyle = getStatusStyle(task.status);
                    const priorityStyle = getPriorityStyle(task.priority);

                    return (
                      <div
                        key={task.id}
                        style={{
                          padding: '0.75rem',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem',
                          pageBreakInside: 'avoid',
                          breakInside: 'avoid'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <strong>{task.title}</strong>
                            {task.due_date && (
                              <span style={{ marginLeft: '0.5rem', color: '#6b7280' }}>
                                • Due: {formatDate(task.due_date, userTimezone)}
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '0.25rem' }}>
                            <span
                              style={{
                                padding: '0.125rem 0.5rem',
                                borderRadius: '9999px',
                                fontSize: '0.625rem',
                                backgroundColor: statusStyle.bg,
                                color: statusStyle.text
                              }}
                            >
                              {statusStyle.label}
                            </span>
                            <span
                              style={{
                                padding: '0.125rem 0.5rem',
                                borderRadius: '9999px',
                                fontSize: '0.625rem',
                                backgroundColor: priorityStyle.bg,
                                color: priorityStyle.text
                              }}
                            >
                              {priorityStyle.label}
                            </span>
                          </div>
                        </div>
                        {task.description && (
                          <div style={{
                            marginTop: '0.5rem',
                            color: '#4b5563',
                            fontSize: '0.75rem',
                            lineHeight: '1.4',
                            whiteSpace: 'pre-wrap'
                          }}>
                            {task.description}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ));
        })()}
      </>
    );
  };

  // Calculate task count for cover page based on view mode
  const getCoverPageTaskCount = () => {
    if (viewMode === 'calendar') {
      // For calendar view, only count tasks visible in the calendar date range
      const getCalendarMonths = () => {
        const months = [];
        const startDate = new Date(calendarDate);

        if (calendarPeriod === '3month') {
          for (let i = 0; i < 3; i++) {
            const monthDate = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
            months.push(monthDate);
          }
        } else if (calendarPeriod === '6month') {
          for (let i = 0; i < 6; i++) {
            const monthDate = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
            months.push(monthDate);
          }
        } else {
          months.push(startDate);
        }

        return months;
      };

      const getMonthDates = (monthDate) => {
        const dates = [];
        const startDate = new Date(monthDate);

        if (calendarPeriod === '5day') {
          const day = startDate.getDay();
          const diff = startDate.getDate() - day + (day === 0 ? -6 : 1);
          startDate.setDate(diff);

          for (let i = 0; i < 5; i++) {
            const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
            dates.push(date);
          }
        } else if (calendarPeriod === '7day') {
          const day = startDate.getDay();
          startDate.setDate(startDate.getDate() - day);

          for (let i = 0; i < 7; i++) {
            const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
            dates.push(date);
          }
        } else {
          // Month view
          const year = startDate.getFullYear();
          const month = startDate.getMonth();
          const firstDay = new Date(year, month, 1);
          const lastDay = new Date(year, month + 1, 0);

          const startDay = new Date(firstDay);
          startDay.setDate(startDay.getDate() - firstDay.getDay());

          let currentDate = new Date(startDay);
          while (currentDate <= lastDay || currentDate.getDay() !== 0) {
            dates.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
            if (dates.length > 42) break;
          }
        }

        return dates;
      };

      const calendarMonths = getCalendarMonths();

      // Get date range - use actual month boundaries, not calendar padding
      let calendarStartDate, calendarEndDate;

      if (calendarPeriod === '5day' || calendarPeriod === '7day') {
        // For week views, use the actual week dates
        const allCalendarDates = calendarMonths.flatMap(month => getMonthDates(month));
        calendarStartDate = new Date(allCalendarDates[0]);
        calendarStartDate.setHours(0, 0, 0, 0); // Normalize to start of day
        calendarEndDate = new Date(allCalendarDates[allCalendarDates.length - 1]);
        calendarEndDate.setHours(23, 59, 59, 999); // Normalize to end of day
      } else {
        // For month views, use actual first and last day of selected months only
        const firstMonth = calendarMonths[0];
        const lastMonth = calendarMonths[calendarMonths.length - 1];

        calendarStartDate = new Date(firstMonth.getFullYear(), firstMonth.getMonth(), 1);
        calendarStartDate.setHours(0, 0, 0, 0); // Normalize to start of day
        calendarEndDate = new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0); // Last day of month
        calendarEndDate.setHours(23, 59, 59, 999); // Normalize to end of day
      }

      // Count tasks visible in selected period
      return tasks.filter(task => {
        if (!task.due_date) return false;
        // Parse date-only string properly without UTC conversion
        const [year, month, day] = task.due_date.split('-').map(Number);
        const taskDate = new Date(year, month - 1, day);
        taskDate.setHours(0, 0, 0, 0);
        return taskDate >= calendarStartDate && taskDate <= calendarEndDate;
      }).length;
    } else if (viewMode === 'gantt') {
      // For Gantt view, count tasks visible in 6-month window (1 past, current, 4 future)
      const now = new Date();
      const ganttStartDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      ganttStartDate.setHours(0, 0, 0, 0);
      const ganttEndDate = new Date(now.getFullYear(), now.getMonth() + 5, 0);
      ganttEndDate.setHours(23, 59, 59, 999);

      return tasks.filter(task => {
        if (!task.due_date && !task.created_at) return false;

        let startDate = task.start_date ? (() => {
          const [year, month, day] = task.start_date.split('-').map(Number);
          return new Date(year, month - 1, day);
        })() : new Date(task.created_at);

        let dueDate;
        if (task.due_date) {
          const [year, month, day] = task.due_date.split('-').map(Number);
          dueDate = new Date(year, month - 1, day);
        } else {
          dueDate = new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000);
        }

        startDate.setHours(0, 0, 0, 0);
        dueDate.setHours(23, 59, 59, 999);

        return dueDate >= ganttStartDate && startDate <= ganttEndDate;
      }).length;
    }

    // For list view, return all filtered tasks
    return tasks.length;
  };

  const coverPageTaskCount = getCoverPageTaskCount();

  return (
    <div className="print-container" data-pdf-ready="true">
      {renderCoverPage(coverPageTaskCount)}
      {viewMode === 'list' && renderListView()}
      {viewMode === 'calendar' && renderCalendarView()}
      {viewMode === 'gantt' && renderGanttView()}
    </div>
  );
};

export default PrintTasks;
